// Beat 4 mad-lib line: skeleton → placeholders → filled (amber-themed).

import React from 'react';
import {
  useMotionValueEvent,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/utilities/cn';
import { getColorStop } from '@/styles/playerColors';
import { toLocalClipVh } from '@/utilities/scrollTimelineMath';

type Token = {
  id: string;
  /** Shown when token is fixed prose. */
  fixed: string;
  /** Placeholder label including punctuation where needed. */
  template: string;
  /** Filled value for placeholder slots; null = fixed token. */
  fill: string | null;
};

const TOKENS: Token[] = [
  { id: 'w1', fixed: 'Me', template: 'Me', fill: null },
  { id: 'w2', fixed: 'trying', template: 'trying', fill: null },
  { id: 'w3', fixed: 'to', template: 'to', fill: null },
  { id: 'w4', fixed: 'explain', template: 'explain', fill: null },
  {
    id: 'concept',
    fixed: '',
    template: '[concept]',
    fill: 'pelvic floor exercises',
  },
  { id: 'w5', fixed: 'to', template: 'to', fill: null },
  {
    id: 'person',
    fixed: '',
    template: '[person]',
    fill: 'DoctorDiggityDank',
  },
  { id: 'w6', fixed: 'with', template: 'with', fill: null },
  { id: 'w7', fixed: 'the', template: 'the', fill: null },
  { id: 'w8', fixed: 'help', template: 'help', fill: null },
  { id: 'w9', fixed: 'of', template: 'of', fill: null },
  {
    id: 'resource',
    fixed: '',
    template: '[resource].',
    fill: 'a traffic cone.',
  },
];

/** Local vh within beat 4 (0–duration): skeleton → template, then placeholder fills. */
const TEMPLATE_START_LOCAL_VH = 10;
const PLACEHOLDER_FILL_START_LOCAL_VH = {
  concept: 18,
  person: 24,
  resource: 30,
} as const;

const fixedWordStyle = {
  color: getColorStop('amber', '950'),
};

const placeholderShellStyle = {
  backgroundColor: getColorStop('amber', '100'),
  color: getColorStop('amber', '900'),
  borderColor: getColorStop('amber', '300'),
};

const filledPlaceholderStyle = {
  backgroundColor: getColorStop('amber', '200'),
  color: getColorStop('amber', '950'),
  borderColor: getColorStop('amber', '400'),
};
const DOC_TWITCH_URL = 'https://www.twitch.tv/doctordiggitydank';

export type BeatFourMadLibCardProps = {
  scrollVh: MotionValue<number>;
  /** Beat segment start in global scroll vh. */
  startVh: number;
  /** Full beat duration (used for phase thresholds). */
  durationVh: number;
};

const BeatFourMadLibCard: React.FC<BeatFourMadLibCardProps> = ({
  scrollVh,
  startVh,
  durationVh,
}) => {
  const localVh = useTransform(scrollVh, (value) =>
    toLocalClipVh(value, startVh, durationVh)
  );
  const initialLocal = toLocalClipVh(scrollVh.get(), startVh, durationVh);
  const [localPhaseVh, setLocalPhaseVh] = React.useState(initialLocal);

  useMotionValueEvent(localVh, 'change', (latest) => {
    setLocalPhaseVh((previous) => (previous === latest ? previous : latest));
  });
  const isSkeletonPhase = localPhaseVh < TEMPLATE_START_LOCAL_VH;

  // Width ladder mirrors the meme image's growth so the card never towers
  // over the image. Short-viewport caps shrink the card on 1280×720 / iPhone
  // SE-class viewports where the dropped card would otherwise crowd the
  // image visually and clip the viewport bottom.
  const titleClass = cn(
    'font-heading text-sm leading-snug sm:text-lg sm:leading-relaxed md:text-xl lg:text-2xl',
    '[@media(max-height:820px)]:text-base',
    '[@media(max-height:820px)_and_(min-width:1024px)]:text-lg',
    '[@media(max-height:720px)]:text-sm'
  );

  return (
    <Card
      className={cn(
        'w-full max-w-3xl border-amber-200/40 bg-amber-50/90 py-2.5 text-amber-950 ring-1 ring-amber-200/35 sm:py-4 md:py-5 lg:py-6',
        '[@media(max-height:820px)]:py-3 [@media(max-height:720px)]:py-2',
        'shadow-sm backdrop-blur-md'
      )}
    >
      <CardContent className="px-3 sm:px-5 md:px-6 lg:px-7 [@media(max-height:720px)]:px-4">
        {isSkeletonPhase ? (
          <p
            className={cn(
              titleClass,
              'flex flex-wrap items-baseline gap-x-1 gap-y-1 sm:gap-x-1.5 sm:gap-y-1.5'
            )}
          >
            {TOKENS.map((token, index) => (
              <React.Fragment key={token.id}>
                <span
                  className="inline-flex h-[1.4em] sm:h-[1.5em] box-border animate-pulse items-center rounded-md border px-2 py-0.5 align-baseline sm:px-2.5"
                  style={{
                    borderColor: getColorStop('amber', '300'),
                    backgroundColor: getColorStop('amber', '100'),
                    animationDelay: `${index * 90}ms`,
                    animationDuration: '1.2s',
                  }}
                >
                  <span className="whitespace-nowrap text-transparent">
                    {token.fill !== null ? token.template : token.fixed}
                  </span>
                </span>
              </React.Fragment>
            ))}
          </p>
        ) : (
          <p
            className={cn(
              titleClass,
              'flex flex-wrap items-baseline gap-x-1 gap-y-1 sm:gap-x-1.5 sm:gap-y-1.5'
            )}
          >
            {TOKENS.map((token) => {
              const isPh = token.fill !== null;
              const fillStartVh =
                token.id === 'concept'
                  ? PLACEHOLDER_FILL_START_LOCAL_VH.concept
                  : token.id === 'person'
                    ? PLACEHOLDER_FILL_START_LOCAL_VH.person
                    : token.id === 'resource'
                      ? PLACEHOLDER_FILL_START_LOCAL_VH.resource
                      : Number.POSITIVE_INFINITY;
              const showFilled = isPh && localPhaseVh >= fillStartVh;
              const segment = isPh ? (
                <span
                  key={token.id}
                  className="inline-flex h-[1.4em] sm:h-[1.5em] box-border items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-center leading-tight sm:px-2.5"
                  style={{
                    ...(showFilled
                      ? filledPlaceholderStyle
                      : placeholderShellStyle),
                  }}
                >
                  {showFilled && token.id === 'person' ? (
                    <a
                      href={DOC_TWITCH_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-current underline-offset-2"
                    >
                      {token.fill}
                    </a>
                  ) : showFilled ? (
                    token.fill
                  ) : (
                    token.template
                  )}
                </span>
              ) : (
                <span
                  key={token.id}
                  className="inline-flex h-[1.4em] sm:h-[1.5em] box-border items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-center leading-tight sm:px-2.5"
                  style={{
                    ...fixedWordStyle,
                    borderColor: getColorStop('amber', '300'),
                    backgroundColor: getColorStop('amber', '50'),
                  }}
                >
                  {token.fixed}
                </span>
              );
              return <React.Fragment key={token.id}>{segment}</React.Fragment>;
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default BeatFourMadLibCard;
